import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';

import { useActiveLink } from 'src/hooks/use-active-link';
import { useBoolean } from 'src/hooks/use-boolean';

import { NavListProps, NavSubListProps } from '../types';

import { NavItem } from './nav-item';

// ----------------------------------------------------------------------

export function NavList({ data, depth, slotProps }: NavListProps) {
  const { pathname } = useLocation();

  const active = useActiveLink(data.path, !!data.children);

  const {
    value: openMenu,
    onFalse: handleCloseMenu,
    onToggle: toggleMenu,
  } = useBoolean(active);

  useEffect(() => {
    if (!active) {
      handleCloseMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      toggleMenu();
    }
  }, [data.children, toggleMenu]);

  return (
    <>
      <NavItem
        open={openMenu}
        onClick={handleToggleMenu}
        //
        title={data.title}
        path={data.path}
        icon={data.icon}
        info={data.info}
        roles={data.roles}
        caption={data.caption}
        disabled={data.disabled}
        //
        depth={depth}
        hasChild={!!data.children}
        externalLink={data.path.includes('http')}
        currentRole={slotProps?.currentRole}
        //
        active={active}
        className={active ? 'active' : ''}
        sx={{
          mb: `${slotProps?.gap}px`,
          ...(depth === 1 ? slotProps?.rootItem : slotProps?.subItem),
        }}
      />

      {!!data.children && (
        <Collapse in={openMenu} unmountOnExit>
          <NavSubList data={data.children} depth={depth} slotProps={slotProps} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function NavSubList({ data, depth, slotProps }: NavSubListProps) {
  return (
    <>
      {data.map((list) => (
        <NavList key={list.title} data={list} depth={depth + 1} slotProps={slotProps} />
      ))}
    </>
  );
}
